import {
  BrandVariants,
  Theme,
  createDarkTheme,
  createLightTheme,
  teamsHighContrastTheme,
} from '@fluentui/react-components';
import { createTheme, IPalette, ISemanticColors, Theme as V8Theme } from '@fluentui/react';
import { GUIDE_FONT_FAMILY, GuidePalette, ThemeMode, guidePalettes } from './guideTokens';

/**
 * Rampa de marca alrededor del primario de la guía: el paso 80 es exactamente #1976d2 y el 60 el
 * primario oscuro #0d47a1; los pasos claros van hacia el azul tenue de los fondos seleccionados.
 */
export const ichtysBrand: BrandVariants = {
  10: '#061529',
  20: '#071f41',
  30: '#092959',
  40: '#0a3371',
  50: '#0c3d89',
  60: '#0d47a1',
  70: '#135fba',
  80: '#1976d2',
  90: '#3285d7',
  100: '#4a94dc',
  110: '#63a2e1',
  120: '#7cb1e6',
  130: '#94c0eb',
  140: '#adcff0',
  150: '#c5ddf5',
  160: '#deecfa',
};

/** Base común de la guía: Lato, 14 px, radio 4 px (el radio circular de las pastillas se mantiene). */
const commonOverrides: Partial<Theme> = {
  fontFamilyBase: GUIDE_FONT_FAMILY,
  fontSizeBase300: '14px',
  borderRadiusMedium: '4px',
};

const statusOverrides = (p: GuidePalette, warningText: string, dangerText: string): Partial<Theme> => ({
  colorStatusSuccessForeground1: p.success,
  colorStatusSuccessForeground3: p.success,
  colorStatusSuccessBorderActive: p.success,
  colorPaletteGreenForeground1: p.success,
  colorPaletteGreenForeground2: p.success,
  colorPaletteGreenForeground3: p.success,
  colorPaletteGreenBorderActive: p.success,

  colorStatusWarningForeground1: warningText,
  colorStatusWarningForeground3: warningText,
  colorStatusWarningBorderActive: p.warning,
  colorPaletteDarkOrangeForeground1: warningText,
  colorPaletteDarkOrangeForeground2: warningText,
  colorPaletteDarkOrangeForeground3: warningText,
  colorPaletteDarkOrangeBorderActive: p.warning,

  colorStatusDangerForeground1: dangerText,
  colorStatusDangerForeground3: dangerText,
  colorStatusDangerBorderActive: dangerText,
  colorPaletteRedForeground1: dangerText,
  colorPaletteRedForeground2: dangerText,
  colorPaletteRedForeground3: dangerText,
  colorPaletteRedBorderActive: dangerText,

  colorPalettePurpleForeground2: p.confirmed,
  colorPalettePurpleBorderActive: p.confirmed,
});

const buildLightTheme = (): Theme => {
  const p = guidePalettes.light;
  return {
    ...createLightTheme(ichtysBrand),
    ...commonOverrides,
    colorNeutralBackground1: p.surface,
    colorNeutralBackground2: p.background,
    colorNeutralStroke1: p.border,
    colorNeutralForeground1: p.text,
    colorNeutralForeground2: p.textSecondary,
    colorNeutralForeground3: p.textSecondary,
    colorNeutralBackgroundDisabled: p.disabledBackground,
    colorBrandBackground: p.primary,
    colorBrandForeground1: p.primary,
    colorBrandForegroundLink: p.accent,
    colorBrandForegroundLinkHover: p.primaryDark,
    colorBrandForegroundLinkPressed: p.primaryDark,
    colorNeutralForegroundOnBrand: p.onPrimary,
    ...statusOverrides(p, p.critical, p.error),
  };
};

const buildDarkTheme = (): Theme => {
  const p = guidePalettes.dark;
  return {
    ...createDarkTheme(ichtysBrand),
    ...commonOverrides,
    colorNeutralBackground1: p.surface,
    colorNeutralBackground2: p.background,
    colorNeutralStroke1: p.border,
    colorNeutralForeground1: p.text,
    colorNeutralForeground2: p.textSecondary,
    colorNeutralForeground3: p.textSecondary,
    colorNeutralBackgroundDisabled: p.disabledBackground,
    colorBrandBackground: p.primary,
    colorBrandForeground1: p.accent,
    colorBrandForeground2: p.accent,
    colorBrandForegroundLink: p.accent,
    colorBrandForegroundLinkHover: p.accent,
    colorBrandForegroundLinkPressed: p.accent,
    colorNeutralForegroundOnBrand: p.onPrimary,
    ...statusOverrides(p, p.warning, p.error),
  };
};

const HC_LEGACY_ACCENTS = ['#1aebff', '#ffff00'];

const buildHighContrastTheme = (): Theme => {
  const p = guidePalettes['high-contrast'];
  // El tema de Teams usa cian (#1aebff) y amarillo (#ffff00) para foco, hover y links: la guía
  // pide un único amarillo #ffe600 para todo eso.
  const base = Object.fromEntries(
    Object.entries(teamsHighContrastTheme).map(([key, value]) => [
      key,
      typeof value === 'string' && HC_LEGACY_ACCENTS.includes(value.toLowerCase()) ? p.primary : value,
    ])
  ) as unknown as Theme;
  return {
    ...base,
    ...commonOverrides,
    colorNeutralBackground1: p.surface,
    colorNeutralBackground2: p.background,
    colorNeutralBackground3: p.background,
    colorNeutralStroke1: p.border,
    colorNeutralForeground1: p.text,
    colorNeutralForeground2: p.textSecondary,
    colorNeutralForeground3: p.textSecondary,
    colorNeutralForegroundDisabled: p.disabledText,
    colorNeutralStrokeDisabled: p.border,
    colorBrandBackground: p.primary,
    colorBrandBackgroundHover: p.primary,
    colorBrandBackgroundPressed: p.primary,
    colorBrandBackgroundSelected: p.primary,
    colorNeutralForegroundOnBrand: p.onPrimary,
    colorBrandForeground1: p.primary,
    colorBrandForeground2: p.primary,
    colorBrandForegroundLink: p.accent,
    colorBrandForegroundLinkHover: p.accent,
    colorBrandForegroundLinkPressed: p.accent,
    colorBrandStroke1: p.primary,
    colorCompoundBrandStroke: p.primary,
    colorCompoundBrandBackground: p.primary,
    colorStrokeFocus1: p.background,
    colorStrokeFocus2: p.primary,
    ...statusOverrides(p, p.warning, p.error),
  };
};

export const ichtysLightTheme: Theme = buildLightTheme();
export const ichtysDarkTheme: Theme = buildDarkTheme();
export const ichtysHighContrastTheme: Theme = buildHighContrastTheme();

export const fluentThemes: Record<ThemeMode, Theme> = {
  light: ichtysLightTheme,
  dark: ichtysDarkTheme,
  'high-contrast': ichtysHighContrastTheme,
};

export const getFluentTheme = (mode: ThemeMode): Theme => fluentThemes[mode] ?? ichtysLightTheme;

/**
 * Tema de Fluent v8 (Pivots, Panels, botones, DetailsList…) mapeado de los mismos valores.
 * En v8 los neutros se invierten en los modos oscuros: `white` es el fondo y `black` el texto.
 */
export const getV8Theme = (mode: ThemeMode): V8Theme => {
  const p = guidePalettes[mode];
  const isLight = mode === 'light';
  const ramp = ichtysBrand;
  const palette: Partial<IPalette> =
    mode === 'high-contrast'
      ? {
          themePrimary: p.primary,
          themeLighterAlt: p.background,
          themeLighter: p.background,
          themeLight: p.primary,
          themeTertiary: p.primary,
          themeSecondary: p.primary,
          themeDarkAlt: p.primary,
          themeDark: p.primary,
          themeDarker: p.primary,
        }
      : {
          themePrimary: p.primary,
          themeLighterAlt: isLight ? ramp[160] : ramp[10],
          themeLighter: isLight ? ramp[150] : ramp[20],
          themeLight: isLight ? ramp[140] : ramp[40],
          themeTertiary: isLight ? ramp[110] : ramp[60],
          themeSecondary: isLight ? ramp[90] : ramp[100],
          themeDarkAlt: isLight ? ramp[70] : p.accent,
          themeDark: isLight ? ramp[60] : p.accent,
          themeDarker: isLight ? ramp[50] : p.accent,
        };
  const neutrals: Partial<IPalette> = isLight
    ? {
        neutralLighterAlt: p.background,
        neutralLighter: '#f3f2f1',
        neutralLight: p.border,
        neutralQuaternaryAlt: p.border,
        neutralQuaternary: '#d0d0d0',
        neutralTertiaryAlt: '#c8c8c8',
        neutralTertiary: '#a19f9d',
        neutralSecondary: p.textSecondary,
        neutralPrimaryAlt: '#3b3a39',
        neutralPrimary: p.text,
        neutralDark: p.text,
        black: '#000000',
        white: p.surface,
      }
    : {
        neutralLighterAlt: p.background,
        neutralLighter: mode === 'dark' ? '#23282e' : p.background,
        neutralLight: p.border,
        neutralQuaternaryAlt: p.border,
        neutralQuaternary: p.border,
        neutralTertiaryAlt: mode === 'dark' ? '#5b6675' : p.textSecondary,
        neutralTertiary: p.textSecondary,
        neutralSecondary: p.textSecondary,
        neutralPrimaryAlt: p.text,
        neutralPrimary: p.text,
        neutralDark: p.text,
        black: p.text,
        white: p.surface,
      };
  const semanticColors: Partial<ISemanticColors> = {
    bodyBackground: p.surface,
    bodyFrameBackground: p.background,
    bodyText: p.text,
    bodySubtext: p.textSecondary,
    link: p.accent,
    linkHovered: p.accent,
    primaryButtonBackground: p.primary,
    primaryButtonBackgroundHovered: mode === 'light' ? p.primaryDark : p.primary,
    primaryButtonText: p.onPrimary,
    primaryButtonTextHovered: p.onPrimary,
    inputBorder: mode === 'high-contrast' ? p.border : undefined,
    errorText: p.error,
    focusBorder: p.primary,
  };
  return createTheme({
    palette: { ...palette, ...neutrals },
    semanticColors: Object.fromEntries(Object.entries(semanticColors).filter(([, v]) => v !== undefined)),
    defaultFontStyle: { fontFamily: GUIDE_FONT_FAMILY },
    isInverted: !isLight,
  });
};
